import {useCallback} from 'react';

import {util} from 'fabric';
import {usePlayerStore} from 'module/player/zustand';
import {useFrameClear, useFrameUpdate} from 'module/sheet/useFrame';
import {useSheet} from 'module/sheet/useSheet';
import {useTextStore} from 'module/text/zustand';
import {useLayoutStore} from 'store';
import storage from 'store/localStorage';

import {useAction} from './canvas-actions';
import {animFrame} from './canvas-animation';
import {canvasReviver, canvasToJson, setBackground} from './canvas-helper';
import {migrate5to6, migrate6to7} from './migrations';
import {enliveObject} from './object-helper';
import {useApiLoad, useApiSave} from './useCanvasApi';
import {useCanvasStore} from './zustand';

const localStoragePrefix = 'sheet';

export const useCanvasLoad = () => {
    const {load: backendLoad, loading} = useApiLoad();

    const canvas = useCanvasStore(state => state.canvas);
    const resetCanvasStates = useCanvasStore(state => state.resetCanvasStates);

    const load = useCallback(async (sheet, isMobile = false) => {
        if (!sheet || !canvas) {
            return;
        }

        let data;

        if (sheet.local) {
            console.debug('load local canvas', sheet.id);
            const jsonStr = storage.getItem(`${localStoragePrefix}_${sheet.id}`);

            if (typeof jsonStr !== 'string') {
                return;
            }

            data = JSON.parse(jsonStr);

        } else {
            console.debug('load remote canvas', sheet.id);
            data = await backendLoad(sheet.id);
        }

        console.debug('load canvas', data);

        if (typeof data !== 'object' || data === null) {
            return;
        }

        canvas.isLoading = true;
        usePlayerStore.getState().unset();
        useTextStore.getState().unset();
        canvas.remove(...canvas.getObjects());
        
        // Apply v5 to v6 migration to all objects before enlivening
        if (data.canvas.objects) {
            console.log('[useCanvasLoad] Migrating', data.canvas.objects.length, 'objects');
            data.canvas.objects.forEach((obj, idx) => {
                console.log(`[useCanvasLoad] Migrating object ${idx}:`, obj.type, obj);
                try {
                    migrate5to6(obj);
                    migrate6to7(obj);
                    console.log(`[useCanvasLoad] Successfully migrated object ${idx}`);
                } catch (e) {
                    console.error(`[useCanvasLoad] Error migrating object ${idx}:`, e);
                    throw e;
                }
            });
            console.log('[useCanvasLoad] Migration complete');
        }
        
        // Fabric.js v6: util.enlivenObjects returns a Promise
        console.log('[useCanvasLoad] Starting enlivenObjects with', data.canvas.objects.length, 'objects');
        
        // Wrap enlivenObjects to catch text creation errors
        try {
            util.enlivenObjects(data.canvas.objects, {
                reviver: (json, element) => {
                    console.log('[canvasReviver] Processing:', json.type, 'id:', json.id || 'no-id');
                    try {
                        const result = canvasReviver(json, element, isMobile);
                        console.log('[canvasReviver] Successfully processed:', json.type);
                        return result;
                    } catch (e) {
                        console.error('[canvasReviver] Error processing', json.type, ':', e);
                        throw e;
                    }
                }
            }).then((objs) => {
                console.log('[useCanvasLoad] enlivenObjects succeeded with', objs.length, 'objects');
                objs.forEach((item) => {
                    enliveObject(item, canvas);
                });
                setBackground(canvas, data.background, () => {
                    resetCanvasStates(canvasToJson(canvas)); // sets dirty to false
                });
                canvas.requestRenderAll(); // Make sure to call once we're ready!
                canvas.isLoading = false;
            }).catch((error) => {
                console.error('[useCanvasLoad] Error enlivening objects:', error);
                console.error('[useCanvasLoad] Error stack:', error.stack);
                console.error('[useCanvasLoad] Error message:', error.message);
                
                // If it's a text split error, try removing all text properties and retry
                if (error.message && error.message.includes('split')) {
                    console.warn('[useCanvasLoad] Text split error detected, attempting recovery...');
                    
                    // Deep clean all text properties
                    const cleanObjects = (objs) => {
                        objs.forEach(obj => {
                            if (obj.type !== 'Text' && obj.type !== 'Textbox' && obj.type !== 'I-text') {
                                delete obj.text;
                            }

                            if (obj.objects && Array.isArray(obj.objects)) {
                                cleanObjects(obj.objects);
                            }
                        });
                    };
                    
                    cleanObjects(data.canvas.objects);
                    
                    // Retry enlivenObjects
                    util.enlivenObjects(data.canvas.objects, {
                        reviver: (json, element) => canvasReviver(json, element, isMobile)
                    }).then((objs) => {
                        console.log('[useCanvasLoad] Recovery succeeded with', objs.length, 'objects');
                        objs.forEach((item) => {
                            enliveObject(item, canvas);
                        });
                        setBackground(canvas, data.background, () => {
                            resetCanvasStates(canvasToJson(canvas));
                        });
                        canvas.requestRenderAll();
                        canvas.isLoading = false;
                    }).catch((retryError) => {
                        console.error('[useCanvasLoad] Recovery failed:', retryError);
                        canvas.isLoading = false;
                    });
                } else {
                    canvas.isLoading = false;
                }
            });
        } catch (syncError) {
            console.error('[useCanvasLoad] Synchronous error:', syncError);
            canvas.isLoading = false;
        }

        return data;
    }, [backendLoad, canvas, resetCanvasStates]);

    return {load, loading};
};

export const useCanvasSave = () => {
    const {frameIndex} = useSheet();

    const {update: updateFrame, loading: loading1} = useFrameUpdate();
    const {save: backendSave, loading: loading2} = useApiSave();

    const canvas = useCanvasStore(state => state.canvas);
    const setDirty = useCanvasStore(state => state.setDirty);
    const showTimeline = useLayoutStore((state) => state.showTimeline);

    const save = useCallback(async (sheet, custom = {}) => {
        if (!sheet || !canvas) {
            return;
        }

        const canvasJson = canvasToJson(canvas);
        delete (canvasJson['backgroundImage']); // do not save backgroundImage

        const data = {
            ...custom,
            width: canvas.width,
            height: canvas.height,
            canvas: canvasJson
        };

        console.log('save canvas',frameIndex,showTimeline, data, sheet);

        if (!showTimeline) {
            if (sheet.local) {
                storage.setItem(`${localStoragePrefix}_${sheet.id}`, JSON.stringify(data));
            } else {
                await backendSave(sheet.id, data);
            }
        } else {
            if (sheet.frames.length > 0) {
                await updateFrame(sheet, animFrame(canvas), frameIndex);
            }
        }

        setDirty(false);
    }, [backendSave, canvas, frameIndex, setDirty, updateFrame, showTimeline]);

    return {save, loading: loading1 || loading2};
};

export const useCanvasClear = () => {
    const setDirty = useCanvasStore(state => state.setDirty);

    const {dispatch} = useAction();

    const {clear: clearFrames, loading} = useFrameClear();

    const clear = useCallback(async (sheet) => {
        await clearFrames(sheet);
        dispatch('clear');
        setDirty(true);
    }, [clearFrames, dispatch, setDirty]);

    return {clear, loading};
};